import { useCallback, useState } from 'react'
import { useModal } from '@ebay/nice-modal-react'
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { useHistory } from 'react-router-dom'
import { Icon } from '@iconify/react'
import {
  Button,
  useContentRefreshDomains,
  useGetProjectDomain,
  useGlobalState,
  useShowMessage,
} from '@pepper/ui'
import { pluralize } from '@pepper/utils/src/function/pluralize'

import SEOTable from 'container/SEOTools/components/SEOTable/SEOTable'
import BottomPopup, { BottomPopupBtn } from 'components/BottomPopup'

import { useKeywordSuggestions } from 'utils/hooks/useKeywordSuggestions'
import { useAddKeywords } from 'utils/hooks/useAddKeywords'
import { useKeywordLabelling } from 'utils/hooks/IdeaLab/useKeywordLabelling'

import { getColumns } from './columns'

import { SuggestedKeyword } from 'types/keyword'

import s from './KeywordSuggestionsTable.module.css'

type Props = {
  companyId: string
  domainId: string
  themeId: string
  projectId?: string
  isDomainRemoved?: boolean
  isContentIdeasPage?: boolean
}

type Intent = 0 | 1 | 2 | 3

const PAGE_LIMIT = 15
const APPLIED_FILTERS_HEIGHT = 48

export const KeywordSuggestionsTable = ({
  companyId,
  projectId,
  domainId,
  themeId,
  isDomainRemoved = false,
  isContentIdeasPage = false,
}: Props) => {
  const history = useHistory()
  const showMessage = useShowMessage()
  const { permissions } = useGlobalState()
  const { show: showUpsellBanner } = useModal('buy-cmp')
  const { show: showProjectPicker } = useModal('select-project-modal')
  const [selectedSuggestionId, setSelectedSuggestionId] = useState('')
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const { data: projectDomain } = useGetProjectDomain({
    data: {
      companyId,
      projectId,
    },
  })

  const [queryParams, setQueryParams] = useQueryParams({
    sug_keyword: withDefault(StringParam, undefined),
    sug_positionLte: withDefault(NumberParam, undefined),
    sug_positionGte: withDefault(NumberParam, undefined),
    sug_page: withDefault(NumberParam, 1),
    sug_limit: withDefault(NumberParam, PAGE_LIMIT),
    sug_exclude_position: withDefault(BooleanParam, false),
    sug_intent: withDefault(NumberParam, undefined),
    'sug_volume:lte': withDefault(NumberParam, undefined),
    'sug_volume:gte': withDefault(NumberParam, undefined),
    'sug_difficulty:lte': withDefault(NumberParam, undefined),
    'sug_difficulty:gte': withDefault(NumberParam, undefined),
    sort: withDefault(StringParam, undefined),
  })

  const {
    sug_positionGte,
    sug_positionLte,
    sug_keyword,
    sug_page,
    sug_limit,
    sug_exclude_position,
    sug_intent,
  } = queryParams

  const { data, isLoading } = useKeywordSuggestions({
    companyId,
    projectId,
    domainId,
    themeId,
    enabled: true,
    queryParams: {
      keyword: sug_keyword?.split(','),
      positionLte: sug_positionLte,
      positionGte: sug_positionGte,
      page: sug_page,
      limit: sug_limit,
      exists: sug_exclude_position,
      gteDifficulty: queryParams['sug_difficulty:gte'],
      lteDifficulty: queryParams['sug_difficulty:lte'],
      lteVolume: queryParams['sug_volume:lte'],
      gteVolume: queryParams['sug_volume:gte'],
      sort: queryParams.sort,
      intent: queryParams.sug_intent as Intent,
    },
  })

  const {
    data: keywordLabelling,
    isLoading: isLoadingKeywordLabel,
    error: labellingError,
  } = useKeywordLabelling({
    id: selectedSuggestionId,
    enabled: selectedSuggestionId.length > 0 && !!companyId,
    companyId,
  })

  const { isGoogleConnected } = useContentRefreshDomains(companyId)

  const { mutateAsync: addKeywords, isLoading: isAdding } = useAddKeywords()

  const suggestionIds = (data?.records ?? []).map(record => record.id)

  const filteredSelectedRows = selectedRowKeys.filter(rowKey => {
    return suggestionIds.includes(`${rowKey}`)
  })

  const getRowKey = useCallback((row: SuggestedKeyword) => {
    return row.id
  }, [])

  const hasKeywordsPermission = permissions?.keywords

  const _addToProject = async (
    _projectId: string,
    _keyword?: SuggestedKeyword
  ) => {
    const keywordIds = _keyword
      ? [_keyword.id]
      : filteredSelectedRows.map(String)
    _keyword && setSelectedRowKeys([_keyword.id])

    const { error } = await addKeywords({
      companyId,
      projectId: _projectId,
      keywordIds,
    })
    if (error) {
      showMessage({ type: 'error', message: error })
    } else {
      if (isContentIdeasPage) {
        showMessage({
          type: 'success',
          message: (
            <div className={s.message}>
              {`${pluralize(keywordIds.length, 'keyword')} added successfully`}
              <Button
                type="default"
                onClick={() => history.push(`/project/${_projectId}/keywords`)}
              >
                Go to project
              </Button>
            </div>
          ),
        })
      } else if (history.location.pathname.includes('topics')) {
        showMessage({
          type: 'success',
          message: (
            <div className={s.message}>
              {`${pluralize(keywordIds.length, 'keyword')} added successfully`}
              <Button
                type="default"
                onClick={() => history.push(`/project/${_projectId}/keywords`)}
              >
                Go to keywords
              </Button>
            </div>
          ),
        })
      } else {
        showMessage({
          type: 'success',
          message: `${pluralize(
            keywordIds.length,
            'keyword'
          )} added to the project`,
        })
      }
    }

    setQueryParams({ sug_page: 1 })
    setSelectedRowKeys([])
  }

  const handleAddToProject = async (_keyword?: SuggestedKeyword) => {
    if (!hasKeywordsPermission) {
      showUpsellBanner({ id: 'keywords' })
      return
    }
    if (!projectId) {
      showProjectPicker({
        onAfterSelectId: async (_projectId: string) =>
          _addToProject(_projectId, _keyword),
        activeDomainId: domainId,
      })
    } else {
      _addToProject(projectId, _keyword)
    }
  }

  const getTableContentHeight = () => {
    const filterHeight =
      !!sug_keyword ||
      sug_exclude_position ||
      sug_intent !== undefined ||
      queryParams['sug_difficulty:gte'] !== undefined ||
      queryParams['sug_volume:gte'] !== undefined
        ? APPLIED_FILTERS_HEIGHT
        : 0

    return isContentIdeasPage
      ? `calc(100vh - ${305 + filterHeight}px)`
      : `calc(100vh - ${234 + filterHeight}px)`
  }

  if (labellingError) {
    showMessage({ type: 'error', message: labellingError })
  }

  return (
    <div className={s.table}>
      <SEOTable
        bordered={false}
        rowKey={getRowKey}
        rowClassName={s.row}
        loading={isLoading || isAdding}
        dataSource={data?.records}
        columns={getColumns({
          handleAddToProject,
          disableCta: filteredSelectedRows.length > 0,
          isLoadingKeywordLabel,
          onGetKeywordLabel: setSelectedSuggestionId,
          pageRankingResults: keywordLabelling,
          domain: projectDomain?.domain.domain_url,
          isGoogleConnected,
          companyId,
          isDomainRemoved,
          isContentIdeasPage,
        })}
        size="small"
        pagination={{
          onChange: pageNumber =>
            setQueryParams({
              sug_page: pageNumber,
            }),
          total: data?.total_records,
          pageSize: sug_limit,
          current: sug_page,
          showSizeChanger: (data?.total_records ?? 0) > PAGE_LIMIT,
          pageSizeOptions: ['15', '30', '50', '100'],
          onShowSizeChange(current, size) {
            setQueryParams({
              sug_page: current,
              sug_limit: size,
            })
          },
        }}
        rowSelection={{
          onChange: setSelectedRowKeys,
          selectedRowKeys: filteredSelectedRows,
        }}
        scroll={{ y: getTableContentHeight(), scrollToFirstRowOnChange: true }}
      />
      <BottomPopup
        align={isContentIdeasPage ? 'center' : 'right'}
        selectedCount={filteredSelectedRows?.length}
        onClose={() => {
          setSelectedRowKeys([])
        }}
      >
        <BottomPopupBtn
          icon={<Icon icon="lucide:plus" width={14} />}
          isLoading={isAdding}
          onClick={() => handleAddToProject()}
        >
          Add to Project
        </BottomPopupBtn>
      </BottomPopup>
    </div>
  )
}
